import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Badge } from './ui/badge';
import { 
  BookOpen, 
  ArrowRight, 
  Volume2, 
  CheckCircle,
  ChevronRight,
  Lightbulb,
  GraduationCap
} from 'lucide-react';

const LearnPossessives = () => {
  const [activeLesson, setActiveLesson] = useState('lesson1');
  const [completedLessons, setCompletedLessons] = useState([]);

  // Possessive endings data - ordered by 1st, 2nd, 3rd person
  const directEndings = [
    // 1st person
    { pronoun: 'My', arabic: 'ana', ending: '-i / -ya', example: 'ktab → ktabi', exampleTranslation: 'book → my book', note: 'Use -ya after vowels', person: '1st' },
    { pronoun: 'Our', arabic: 'hna', ending: '-na', example: 'bled → bledna', exampleTranslation: 'country → our country', note: '', person: '1st' },
    // 2nd person
    { pronoun: 'Your (sg.)', arabic: 'nta/nti', ending: '-ek / -k', example: 'dar → darek', exampleTranslation: 'house → your house', note: 'Use -k after vowels', person: '2nd' },
    { pronoun: 'Your (pl.)', arabic: 'ntouma', ending: '-koum', example: 'dar → darkoum', exampleTranslation: 'house → your (pl.) house', note: '', person: '2nd' },
    // 3rd person
    { pronoun: 'His', arabic: 'huwa', ending: '-ou / -h', example: 'ktab → ktabou', exampleTranslation: 'book → his book', note: 'Use -h after vowels', person: '3rd' },
    { pronoun: 'Her', arabic: 'hiya', ending: '-ha', example: 'dar → darha', exampleTranslation: 'house → her house', note: '', person: '3rd' },
    { pronoun: 'Their', arabic: 'houma', ending: '-houm', example: 'weld → weldhoum', exampleTranslation: 'son → their son', note: '', person: '3rd' },
  ];

  const indirectEndings = [
    // 1st person
    { pronoun: 'My', pattern: 'diyal-i / ta3-i', example: 'telefon diyali', exampleTranslation: 'my phone', person: '1st' },
    { pronoun: 'Our', pattern: 'diyal-na / ta3-na', example: 'telefon diyalna', exampleTranslation: 'our phone', person: '1st' },
    // 2nd person
    { pronoun: 'Your (sg.)', pattern: 'diyal-ek / ta3-ek', example: 'telefon diyalek', exampleTranslation: 'your phone', person: '2nd' },
    { pronoun: 'Your (pl.)', pattern: 'diyal-koum / ta3-koum', example: 'telefon diyalkoum', exampleTranslation: 'your (pl.) phone', person: '2nd' },
    // 3rd person
    { pronoun: 'His', pattern: 'diyal-ou / ta3-ou', example: 'telefon diyalou', exampleTranslation: 'his phone', person: '3rd' },
    { pronoun: 'Her', pattern: 'diyal-ha / ta3-ha', example: 'telefon diyalha', exampleTranslation: 'her phone', person: '3rd' },
    { pronoun: 'Their', pattern: 'diyal-houm / ta3-houm', example: 'telefon diyalhoum', exampleTranslation: 'their phone', person: '3rd' },
  ];

  const markLessonComplete = (lesson) => {
    if (!completedLessons.includes(lesson)) {
      setCompletedLessons([...completedLessons, lesson]);
    }
  };

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      {/* Header */}
      <div className="text-center mb-8">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-green-500 to-emerald-600 rounded-full mb-4">
          <BookOpen className="w-8 h-8 text-white" />
        </div>
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Learn Possessives</h1>
        <p className="text-gray-600">Master how to express ownership in Algerian Arabic</p>
      </div>

      {/* Progress */}
      <div className="flex justify-center gap-4 mb-8">
        <Badge variant={completedLessons.includes('lesson1') ? 'default' : 'outline'} className="flex items-center gap-1">
          {completedLessons.includes('lesson1') && <CheckCircle className="w-3 h-3" />}
          Lesson 1
        </Badge>
        <Badge variant={completedLessons.includes('lesson2') ? 'default' : 'outline'} className="flex items-center gap-1">
          {completedLessons.includes('lesson2') && <CheckCircle className="w-3 h-3" />}
          Lesson 2
        </Badge>
      </div>

      {/* Tabs for Lessons */}
      <Tabs value={activeLesson} onValueChange={setActiveLesson} className="w-full">
        <TabsList className="grid w-full grid-cols-2 mb-6">
          <TabsTrigger value="lesson1" className="flex items-center gap-2">
            <span className="hidden sm:inline">Lesson 1:</span> Direct Endings
          </TabsTrigger>
          <TabsTrigger value="lesson2" className="flex items-center gap-2">
            <span className="hidden sm:inline">Lesson 2:</span> Diyal / Ta3
          </TabsTrigger>
        </TabsList>

        {/* Lesson 1: Direct Endings */}
        <TabsContent value="lesson1">
          <Card className="mb-6">
            <CardHeader className="bg-gradient-to-r from-green-50 to-emerald-50">
              <CardTitle className="flex items-center gap-2">
                <GraduationCap className="w-5 h-5 text-green-600" />
                Lesson 1: Direct Possessive Endings
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              {/* Introduction */}
              <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-6 rounded-r-lg">
                <div className="flex items-start gap-2">
                  <Lightbulb className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-blue-900">How it works:</p>
                    <p className="text-blue-800 text-sm mt-1">
                      In Algerian Arabic, many nouns form possessives by adding an ending directly to the word.
                      Simply attach the appropriate ending to show who owns something.
                    </p>
                  </div>
                </div>
              </div>

              {/* Formula */}
              <div className="text-center mb-6 p-4 bg-gray-50 rounded-lg">
                <p className="text-lg font-semibold text-gray-700">
                  <span className="text-green-600">Noun</span> + <span className="text-red-600">Ending</span> = <span className="text-purple-600">Possessive</span>
                </p>
                <p className="text-sm text-gray-500 mt-1">Example: ktab + i = ktabi (my book)</p>
              </div>

              {/* Special Rules */}
              <div className="space-y-4 mb-6">
                {/* Ta Marbuta Rule */}
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <h4 className="font-semibold text-amber-900 mb-2 flex items-center gap-2">
                    <span className="text-lg">📝</span> Important Rule: Nouns ending in &apos;a&apos;
                  </h4>
                  <p className="text-amber-800 text-sm mb-2">
                    When a noun ends in <strong>&apos;a&apos;</strong> (Arabic ta marbuta ة), the <strong>&apos;a&apos; changes to &apos;t&apos;</strong> before adding the possessive ending.
                  </p>
                  <div className="bg-white rounded p-3 mt-2">
                    <p className="font-mono text-sm">
                      <span className="text-gray-600">Example:</span> 3em<span className="text-red-600 font-bold">a</span> (paternal auntie) → 3em<span className="text-green-600 font-bold">t</span>i (my paternal auntie)
                    </p>
                    <p className="font-mono text-sm mt-1">
                      <span className="text-gray-600">Example:</span> khedm<span className="text-red-600 font-bold">a</span> (work/job) → khedm<span className="text-green-600 font-bold">t</span>i (my work/job)
                    </p>
                  </div>
                </div>

                {/* Mum & Dad Special Case */}
                <div className="bg-pink-50 border border-pink-200 rounded-lg p-4">
                  <h4 className="font-semibold text-pink-900 mb-2 flex items-center gap-2">
                    <span className="text-lg">👨‍👩‍👧</span> Special Case: Mum &amp; Dad
                  </h4>
                  <p className="text-pink-800 text-sm mb-3">
                    The words <strong>mama, papa, yima, beba, baba</strong> already mean &quot;my mum/dad&quot; - the possessive is built in!
                  </p>
                  <p className="text-pink-800 text-sm mb-2">
                    For other people&apos;s mum/dad, use <strong>yima</strong> and <strong>beba</strong>, but they change form:
                  </p>
                  <div className="bg-white rounded p-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <div>
                      <p className="font-semibold text-pink-700 text-sm mb-1">Mum:</p>
                      <p className="font-mono text-sm">yima → yim<span className="text-green-600 font-bold">e</span>-</p>
                      <p className="text-xs text-gray-500 mt-1">yimek (your mum), yimeha (her mum)</p>
                    </div>
                    <div>
                      <p className="font-semibold text-pink-700 text-sm mb-1">Dad:</p>
                      <p className="font-mono text-sm">beba → beb<span className="text-green-600 font-bold">e</span>-</p>
                      <p className="text-xs text-gray-500 mt-1">bebek (your dad), bebeha (her dad)</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Endings Table */}
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-green-100">
                      <th className="border border-green-200 px-4 py-3 text-left font-semibold text-green-900">Pronoun</th>
                      <th className="border border-green-200 px-4 py-3 text-left font-semibold text-green-900">Ending</th>
                      <th className="border border-green-200 px-4 py-3 text-left font-semibold text-green-900">Example</th>
                      <th className="border border-green-200 px-4 py-3 text-left font-semibold text-green-900 hidden sm:table-cell">Note</th>
                    </tr>
                  </thead>
                  <tbody>
                    {directEndings.map((item, index) => (
                      <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                        <td className="border border-gray-200 px-4 py-3">
                          <span className="font-medium">{item.pronoun}</span>
                          <span className="text-gray-500 text-sm ml-1">({item.arabic})</span>
                        </td>
                        <td className="border border-gray-200 px-4 py-3">
                          <span className="font-mono text-lg text-red-600 font-bold">{item.ending}</span>
                        </td>
                        <td className="border border-gray-200 px-4 py-3">
                          <div className="font-medium text-purple-700">{item.example}</div>
                          <div className="text-sm text-gray-500">{item.exampleTranslation}</div>
                        </td>
                        <td className="border border-gray-200 px-4 py-3 text-sm text-gray-600 hidden sm:table-cell">
                          {item.note || '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Practice Examples */}
              <div className="mt-6 p-4 bg-amber-50 rounded-lg border border-amber-200">
                <h4 className="font-semibold text-amber-900 mb-3 flex items-center gap-2">
                  <Volume2 className="w-4 h-4" />
                  Practice Examples with &quot;telefon&quot; (phone)
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  <div className="bg-white p-3 rounded border">
                    <p className="font-medium">telefon + i = <span className="text-green-600">telefoni</span></p>
                    <p className="text-sm text-gray-500">my phone</p>
                  </div>
                  <div className="bg-white p-3 rounded border">
                    <p className="font-medium">telefon + na = <span className="text-green-600">telefonna</span></p>
                    <p className="text-sm text-gray-500">our phone</p>
                  </div>
                  <div className="bg-white p-3 rounded border">
                    <p className="font-medium">telefon + ek = <span className="text-green-600">telefonek</span></p>
                    <p className="text-sm text-gray-500">your phone</p>
                  </div>
                  <div className="bg-white p-3 rounded border">
                    <p className="font-medium">telefon + koum = <span className="text-green-600">telefonkoum</span></p>
                    <p className="text-sm text-gray-500">your (pl.) phone</p>
                  </div>
                  <div className="bg-white p-3 rounded border">
                    <p className="font-medium">telefon + ou = <span className="text-green-600">telefonou</span></p>
                    <p className="text-sm text-gray-500">his phone</p>
                  </div>
                  <div className="bg-white p-3 rounded border">
                    <p className="font-medium">telefon + ha = <span className="text-green-600">telefonha</span></p>
                    <p className="text-sm text-gray-500">her phone</p>
                  </div>
                  <div className="bg-white p-3 rounded border sm:col-span-2 lg:col-span-1">
                    <p className="font-medium">telefon + houm = <span className="text-green-600">telefonhoum</span></p>
                    <p className="text-sm text-gray-500">their phone</p>
                  </div>
                </div>
              </div>

              {/* Complete Lesson Button */}
              <div className="mt-6 flex justify-between items-center">
                <Button 
                  variant="outline" 
                  onClick={() => markLessonComplete('lesson1')}
                  disabled={completedLessons.includes('lesson1')}
                  className="flex items-center gap-2"
                >
                  {completedLessons.includes('lesson1') ? (
                    <>
                      <CheckCircle className="w-4 h-4 text-green-600" />
                      Completed
                    </>
                  ) : (
                    <>
                      Mark as Complete
                    </>
                  )}
                </Button>
                <Button onClick={() => setActiveLesson('lesson2')} className="flex items-center gap-2">
                  Next: Diyal / Ta3
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Lesson 2: Diyal/Ta3 */}
        <TabsContent value="lesson2">
          <Card className="mb-6">
            <CardHeader className="bg-gradient-to-r from-purple-50 to-pink-50">
              <CardTitle className="flex items-center gap-2">
                <GraduationCap className="w-5 h-5 text-purple-600" />
                Lesson 2: Diyal / Ta3 Pattern
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              {/* Introduction */}
              <div className="bg-purple-50 border-l-4 border-purple-500 p-4 mb-6 rounded-r-lg">
                <div className="flex items-start gap-2">
                  <Lightbulb className="w-5 h-5 text-purple-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-purple-900">When to use this pattern:</p>
                    <p className="text-purple-800 text-sm mt-1">
                      Some nouns (especially loanwords) do not take direct endings. Instead, they use 
                      <strong> diyal-</strong> or <strong>ta3-</strong> (meaning &quot;of&quot; or &quot;belonging to&quot;) followed by the possessive ending.
                      These two words are interchangeable.
                    </p>
                  </div>
                </div>
              </div>

              {/* Formula */}
              <div className="text-center mb-6 p-4 bg-gray-50 rounded-lg">
                <p className="text-lg font-semibold text-gray-700">
                  <span className="text-green-600">Noun</span> + <span className="text-purple-600">diyal/ta3</span> + <span className="text-red-600">ending</span>
                </p>
                <p className="text-sm text-gray-500 mt-1">Example: telefon + diyali = telefon diyali (my phone)</p>
              </div>

              {/* Tip Box */}
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
                <p className="text-amber-900 font-medium mb-1">💡 Good to know:</p>
                <p className="text-amber-800 text-sm">
                  <strong>diyal-</strong> and <strong>ta3-</strong> are completely interchangeable. 
                  You can say &quot;telefon diyali&quot; or &quot;telefon ta3i&quot; - both mean &quot;my phone&quot;!
                </p>
              </div>

              {/* Endings Table */}
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-purple-100">
                      <th className="border border-purple-200 px-4 py-3 text-left font-semibold text-purple-900">Pronoun</th>
                      <th className="border border-purple-200 px-4 py-3 text-left font-semibold text-purple-900">Pattern</th>
                      <th className="border border-purple-200 px-4 py-3 text-left font-semibold text-purple-900">Example</th>
                    </tr>
                  </thead>
                  <tbody>
                    {indirectEndings.map((item, index) => (
                      <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                        <td className="border border-gray-200 px-4 py-3 font-medium">{item.pronoun}</td>
                        <td className="border border-gray-200 px-4 py-3">
                          <span className="font-mono text-purple-600 font-bold">{item.pattern}</span>
                        </td>
                        <td className="border border-gray-200 px-4 py-3">
                          <div className="font-medium text-green-700">{item.example}</div>
                          <div className="text-sm text-gray-500">{item.exampleTranslation}</div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Practice Examples */}
              <div className="mt-6 p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                <h4 className="font-semibold text-emerald-900 mb-3 flex items-center gap-2">
                  <Volume2 className="w-4 h-4" />
                  Practice Examples
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="bg-white p-3 rounded border">
                    <p className="font-medium">telefon <span className="text-purple-600">diyali</span></p>
                    <p className="text-sm text-gray-500">my phone</p>
                  </div>
                  <div className="bg-white p-3 rounded border">
                    <p className="font-medium">tonobil <span className="text-purple-600">ta3ek</span></p>
                    <p className="text-sm text-gray-500">your car</p>
                  </div>
                  <div className="bg-white p-3 rounded border">
                    <p className="font-medium">ordinateur <span className="text-purple-600">diyalha</span></p>
                    <p className="text-sm text-gray-500">her computer</p>
                  </div>
                  <div className="bg-white p-3 rounded border">
                    <p className="font-medium">sac <span className="text-purple-600">ta3na</span></p>
                    <p className="text-sm text-gray-500">our bag</p>
                  </div>
                </div>
              </div>

              {/* Complete Lesson Button */}
              <div className="mt-6 flex justify-between items-center">
                <Button 
                  variant="outline" 
                  onClick={() => markLessonComplete('lesson2')}
                  disabled={completedLessons.includes('lesson2')}
                  className="flex items-center gap-2"
                >
                  {completedLessons.includes('lesson2') ? (
                    <>
                      <CheckCircle className="w-4 h-4 text-green-600" />
                      Completed
                    </>
                  ) : (
                    <>
                      Mark as Complete
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Ready to Play */}
      <Card className="bg-gradient-to-r from-green-500 to-emerald-600 text-white">
        <CardContent className="p-6 text-center">
          <h3 className="text-xl font-bold mb-2">Ready to Practice?</h3>
          <p className="text-green-100 mb-4">
            Test your knowledge with our interactive Possessives Game!
          </p>
          <Link to="/games/possessives">
            <Button variant="secondary" size="lg" className="flex items-center gap-2 mx-auto">
              Play Possessives Game
              <ArrowRight className="w-5 h-5" />
            </Button>
          </Link>
        </CardContent>
      </Card>
    </div>
  );
};

export default LearnPossessives;
